// @ts-nocheck
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Modal, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { alertDialogAciton } from "../../../store/actions/auth";
import { isActiveRole } from "../../../utils/methods/storeFunc";
import { USER_ROLES } from "../../../staticData/userRoles";
import {
  acceptServiceAgreement,
  getServiceAgreementStatus,
  type ServiceAgreementStatus,
} from "../../../api/clientAgreements";
import {
  ServiceAgreementAcceptance,
  emptyServiceAgreementValue,
  serviceAgreementYupShape,
} from "../../ServiceAgreement/ServiceAgreementAcceptance";

/**
 * Existing clients sign the Service Agreement "before their next show"
 * (finance item 10, 2026-09-24): this banner sits at the top of every client
 * page while the company has not accepted the current version. It never
 * blocks navigation — the backend gate is what blocks estimate approval and
 * crewing — it just gives the client the place to sign.
 */
const modalStyle = {
  m: "auto",
  width: "min(96vw, 900px)",
  maxHeight: "94vh",
  overflowY: "auto",
  boxShadow: 24,
  borderRadius: "16px",
  padding: "20px",
  border: "2px solid #D175B6",
  backgroundColor: "#000",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

export const ServiceAgreementGate = () => {
  const dispatch = useDispatch();
  const isClient = isActiveRole(USER_ROLES.CLIENT);
  const [status, setStatus] = useState<ServiceAgreementStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(emptyServiceAgreementValue());
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!isClient) return;
    const { response } = await getServiceAgreementStatus();
    setStatus(response?.data?.data ?? response?.data ?? null);
  }, [isClient]);

  useEffect(() => {
    load();
  }, [load]);

  const validate = async (next) => {
    try {
      await serviceAgreementYupShape(Yup).validate(next, { abortEarly: false });
      setErrors({});
      return true;
    } catch (e) {
      const collected = {};
      (e?.inner || []).forEach((err) => {
        if (err?.path && !collected[err.path]) collected[err.path] = err.message;
      });
      setErrors(collected);
      return false;
    }
  };

  const submit = async () => {
    setTouched({ agree: true, signerName: true, signerTitle: true });
    if (!(await validate(value))) return;
    setSaving(true);
    const { response, error } = await acceptServiceAgreement({
      agree: value.agree,
      signerName: value.signerName.trim(),
      signerTitle: value.signerTitle.trim(),
    });
    setSaving(false);
    if (error) {
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Error",
          message: error?.response?.data?.message || "Could not record your acceptance. Please try again.",
        }),
      );
      return;
    }
    const data = response?.data?.data ?? response?.data;
    setOpen(false);
    setStatus((prev) => ({ ...(prev || {}), required: false, outdated: false, signedVersion: data?.version ?? prev?.currentVersion }));
    dispatch(
      alertDialogAciton({
        open: true,
        title: "Service Agreement signed",
        message: `Thank you. The ShowPhaze Service Agreement was accepted${
          data?.companyName ? ` for ${data.companyName}` : ""
        }. A signed copy is on its way to your e-mail.`,
      }),
    );
  };

  if (!isClient || !status?.applicable || !status?.required) return null;

  return (
    <>
      <Alert
        severity="warning"
        sx={{
          margin: "0 0 12px 0",
          borderRadius: "12px",
          border: "1px solid #D175B6",
          background: "rgba(209, 117, 182, 0.12)",
          color: "#fff",
          "& .MuiAlert-icon": { color: "#D175B6" },
          alignItems: "center",
        }}
        action={
          <Button
            size="small"
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{ background: "#D175B6", color: "#000", fontWeight: 700, "&:hover": { background: "#e08cc6" }, whiteSpace: "nowrap" }}
          >
            Review and sign
          </Button>
        }
      >
        <Typography sx={{ fontSize: "14px", color: "#fff" }}>
          <strong>Service Agreement required.</strong>{" "}
          {status.outdated
            ? "The ShowPhaze Service Agreement was updated and the new version must be accepted for your company."
            : "Your company has not signed the ShowPhaze Service Agreement yet."}{" "}
          Please have an authorized company representative review and sign the current agreement.
        </Typography>
      </Alert>

      <Modal open={open} onClose={() => (saving ? null : setOpen(false))}>
        <Box sx={modalStyle}>
          <ServiceAgreementAcceptance
            value={value}
            onChange={(next) => {
              setValue(next);
              if (Object.keys(touched).length) validate(next);
            }}
            errors={errors}
            touched={touched}
            onBlur={(field) => setTouched((t) => ({ ...t, [field]: true }))}
            disabled={saving}
            companyName={status.companyName || undefined}
            viewerHeight="52vh"
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "16px" }}>
            <Button variant="outlined" disabled={saving} onClick={() => setOpen(false)} sx={{ color: "#fff", borderColor: "#fff" }}>
              Later
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={submit}
              sx={{ background: "#D175B6", color: "#000", fontWeight: 700, minWidth: "160px", "&:hover": { background: "#e08cc6" } }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: "#000" }} /> : "I agree and sign"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ServiceAgreementGate;
